// =========================================================================
// Wallet-system convergence contract.
//
// The single seam where the custody layer meets everything else: the signer,
// the BFF Worker, the Mini App, the Formance ledger, and KYT. Plain ESM so
// Node (signer), Wrangler (BFF) and Vite (Mini App) can all import it with no
// build step. Types live alongside in wallet.d.mts.
//
// Rule of thumb: if two subsystems need to agree on a string, an amount, or a
// shape, it is defined HERE and imported — never re-typed on each side.
// =========================================================================

// ---- entities & assets ---------------------------------------------------

// One entity in custody: indiaotc was discarded (operator, 4 Oct 2026 — merge decision D14).
export const ENTITIES = ['dubaiotc'];

// Base-unit arithmetic only. Floats never touch money anywhere in this system.
// Keyed by MAIN'S asset codes — admin.asset_registry is the source (db/0003,
// 0015, 0023: "USDTTRC", not "USDT-TRC20"); the custody tables reference it by
// foreign key, so a code not in it is refused at the row. Coins are added here
// one at a time as each is built (operator, 16 Oct 2026: "start with TRC USDT
// and then integrate one by one"). TRX and ETH are the chains' own coins, paid
// for activations and fees; they are never a deposit.
export const ASSETS = Object.freeze({
  USDTTRC: { decimals: 6, family: 'crypto', chain: 'TRC20', token: 'USDT' },
  USDCTRC: { decimals: 6, family: 'crypto', chain: 'TRC20', token: 'USDC' },
  USDTERC: { decimals: 6, family: 'crypto', chain: 'ERC20', token: 'USDT' },
  USDCERC: { decimals: 6, family: 'crypto', chain: 'ERC20', token: 'USDC' },
  TRX: { decimals: 6, family: 'crypto', chain: 'TRC20', token: 'TRX' },    // 1 TRX = 1e6 sun
  ETH: { decimals: 18, family: 'crypto', chain: 'ERC20', token: 'ETH' },   // 1 ETH = 1e18 wei
});
/** The coins the wallet moves today, in the order they were built. */
export const BUILT_ASSETS = Object.freeze(['USDTTRC']);

export function assetDecimals(asset) {
  const a = ASSETS[asset];
  if (!a) throw new Error(`unknown asset: ${asset}`);
  return BigInt(a.decimals);
}

// Decimal string ("12.345678") → bigint base units. Rejects anything lossy.
export function toBaseUnits(asset, decimalString) {
  const dp = assetDecimals(asset);
  const s = String(decimalString).trim();
  if (!/^-?\d+(\.\d+)?$/.test(s)) throw new Error(`not a decimal amount: ${decimalString}`);
  const neg = s.startsWith('-');
  const [whole, frac = ''] = (neg ? s.slice(1) : s).split('.');
  if (BigInt(frac.length) > dp) throw new Error(`${asset} allows at most ${dp} decimal places`);
  const padded = frac.padEnd(Number(dp), '0');
  const v = BigInt(whole) * 10n ** dp + BigInt(padded || '0');
  return neg ? -v : v;
}

// bigint base units → decimal string, exact (no rounding, no float).
export function fromBaseUnits(asset, units) {
  const dp = Number(assetDecimals(asset));
  const neg = units < 0n;
  const abs = neg ? -units : units;
  const s = abs.toString().padStart(dp + 1, '0');
  const whole = s.slice(0, s.length - dp);
  const frac = dp ? s.slice(s.length - dp).replace(/0+$/, '') : '';
  return `${neg ? '-' : ''}${whole}${frac ? '.' + frac : ''}`;
}

// ---- wallet topology -----------------------------------------------------
//
// Every address is one row of main's register, admin.party_wallets (decision
// D5), and what it is FOR is the register's `class`. The old branch's layers
// (L0, L1, L2A, L2M, L3, Q, FEE) map onto these; the operator's own words for
// them are in the right-hand column (3 and 5 Oct 2026).
//
//   deposit          an address issued to one customer or one deal   (L0)
//   running_hot      the clean float that pays customers              (L1 clean, L2A, L2M)
//   dirty_hot        scores 50–69: isolated, pays merchants only      (L1 dirty)
//   frozen           70+ or a sanctions/darknet hit; held             (Q)
//   treasury         the bulk; two of three Ledgers                   (L3)
//   fee              network fees; the one server-held key            (FEE)
//   exchange_deposit the desk's deposit address at a venue
//   customer_payout  the customer's own address the desk pays to
//
// No class for "an address the customer sent from" (customer_source, dropped
// 16 Oct on the operator's word): every deal gets a fresh deposit address, so
// there is nothing to keep about where the money came from beyond the deposit.
export const CLASSES = Object.freeze([
  'deposit', 'running_hot', 'dirty_hot', 'frozen', 'treasury', 'fee',
  'exchange_deposit', 'customer_payout',
]);
/**
 * The class, as it is said and shown — the ONE map every screen, the signing
 * page's names and the audit words come from. The operator chose these on
 * 16 Oct 2026 ("don't use blocked/frozen etc"): wallets are named for what
 * they do; the risk score is a colour band on the money (green under 50,
 * amber 50–69, red 70+), not a wallet's name. "Flagged" is the one word kept
 * from the risk vocabulary — every compliance tool's word for a hit, and the
 * verdict the system records (clear / review / flagged). Since N-091 (30 Oct
 * 2026, docs/TERMINOLOGY.md §2) these are the group words of a wallet's name:
 * Deposit and Fee, not "Customer deposit" and "Network fees".
 */
export const CLASS_WORDS = Object.freeze({
  deposit: 'Deposit',
  running_hot: 'Hot',
  dirty_hot: 'Merchant Hot',
  frozen: 'Flagged',
  treasury: 'Treasury',
  fee: 'Fee',
  exchange_deposit: 'Exchange deposit',
  customer_payout: 'Payout address',
});
/**
 * Why a deposit's Books entry waits, in the words the wallet's Books poster
 * keeps on the posting (custody_books_postings.last_error, after the door's
 * status: "400 …", "409 …"; before Mechanisms' B1 the untagged words were a
 * hold, kept bare). The poster writes them (orchestrator/src/books/poster.mjs);
 * the Books server reads them back as a deposit row's `books.why`
 * (bff/src/db/custody.ts). One copy, so the two cannot drift (2 Nov 2026).
 */
export const BOOKS_WAITING = Object.freeze({
  untagged: 'the deposit address names no customer — tag it in the wallet; do not book it on the Chain screen',
  mixed: 'it carried desk money and customer money together — book it by hand on the Chain screen',
});
/** The classes the desk holds keys for. */
export const DESK_CLASSES = Object.freeze(['deposit', 'running_hot', 'dirty_hot', 'frozen', 'treasury', 'fee']);
/**
 * The desk classes whose ACTIVE address the orchestrator reads for the
 * Balances page (runtime/renewals.mjs readActive): every desk class but the
 * deposit addresses — those are many, one per payment, and the watcher's.
 */
export const READ_CLASSES = Object.freeze(DESK_CLASSES.filter((c) => c !== 'deposit'));
/** How often the orchestrator reads them: ACTIVE_BALANCE_CHECK_MS's default. */
export const ACTIVE_READ_MS = 3 * 60_000;
/**
 * When the dashboard stops calling such a reading current: the next read is overdue — one read
 * interval, a tick (TICK_MS) and the pass itself. It was 2 min under a 3-min read, so the Balances page
 * said "Figures not current" one minute in every three (found 28 Oct 2026); the orchestrator now
 * refuses a read interval that would cross it.
 */
export const BALANCE_STALE_AFTER_MS = ACTIVE_READ_MS + 60_000;
/** How often it reads a retired float address for a late arrival: RETIRED_BALANCE_CHECK_MS's default. */
export const RETIRED_READ_MS = 15 * 60_000;
/** How often it checks the Fee float, and writes what it saw for the dashboard (db/0110): FLOAT_CHECK_MS's default. */
export const FLOAT_READ_MS = 5 * 60_000;
/**
 * When the dashboard stops believing what the wallet server last wrote (P-072): two of its checks missed, at the pace
 * the row itself states (`everyMs`), and a minute for the check. Older than that, a stall or an activation count is
 * said as not known — a "not stalled" from an hour ago is a claim nobody is making now.
 */
export const runtimeStatusStaleAfterMs = (everyMs) => 2 * (Number.isFinite(everyMs) && everyMs > 0 ? everyMs : FLOAT_READ_MS) + 60_000;
/**
 * A retired address's reading, marked against its own pace — the Keys screen's Retire confirm sums every address a
 * key issued: stale once its next read is overdue, blind once a second is missed. Marked against the 3-min pace, a
 * key's old addresses fell out of its total about 11 minutes in every 15 (found 28 Oct 2026).
 */
export const RETIRED_STALE_AFTER_MS = RETIRED_READ_MS + 60_000;
export const RETIRED_BLIND_AFTER_MS = 2 * RETIRED_READ_MS;
/**
 * Which Ledger account (m/44'/195'/N') holds which class's addresses — the
 * desk's one convention for its account keys (slice E, 18 Oct 2026). Account
 * 0 is the deposit key, whose first address is also the registered device;
 * the three floats renew from their own accounts, so a renewed address is
 * signed by its own key at its own path.
 */
export const KEY_ACCOUNT_CLASS = Object.freeze({ 0: 'deposit', 1: 'running_hot', 2: 'dirty_hot', 3: 'treasury' });
/** The classes that renew themselves (admin.custody_renewals), and the operator's intervals (17 Oct 2026). */
export const RENEWABLE_CLASSES = Object.freeze(['running_hot', 'dirty_hot', 'treasury']);
export const RENEWAL_DEFAULT_DAYS = Object.freeze({ running_hot: 15, dirty_hot: 3, treasury: 30 });
/** Every word a signing card may be called (admin.custody_signing_requests.kind). */
export const CARD_KINDS = Object.freeze(['sweep', 'payout', 'refill', 'vault_move', 'quarantine_move', 'renewal_move', 'release']);
/**
 * How a desk class is signed (admin.custody_wallets.signing):
 *   ledger         the desk Ledger alone — one tap
 *   multisig_2of3  two of three Ledgers
 *   server         the one server-held key: the fee wallet, and nothing else
 */
export const CLASS_SIGNING = Object.freeze({
  deposit: 'ledger',
  running_hot: 'ledger',
  dirty_hot: 'ledger',
  frozen: 'multisig_2of3',
  treasury: 'multisig_2of3',
  fee: 'server',
});
/** Whether moving money out of this class needs a person at a Ledger. */
export function requiresCosign(cls) {
  if (!(cls in CLASS_SIGNING)) throw new Error(`not a desk class: ${cls}`);
  return CLASS_SIGNING[cls] !== 'server';
}
/** Where screening sends a deposit — the pipeline's pools — and the class that pool is. */
export const POOLS = Object.freeze({ CLEAN: 'clean', DIRTY: 'dirty', QUARANTINE: 'quarantine' });
export const POOL_CLASS = Object.freeze({ clean: 'running_hot', dirty: 'dirty_hot', quarantine: 'frozen' });
// ---- Formance account addressing ----------------------------------------
//
// PROVISIONAL — custody ledger postings are decision D12, open: the ledger
// name, the account paths and the asset codes must align with
// docs/LEDGER-STANDARD.md before the wallet posts anything (convergence
// audit, N1). Nothing posts today. What is fixed already: the ledger sees
// THREE crypto treasuries per asset, not one per class. Moving between the
// hot classes is our own money shuffling and posts nothing; treasury and
// frozen are economically distinct, so they get their own accounts.
export const TREASURY_HOT = (asset) => `@treasury:crypto:${asset.toLowerCase()}`;
export const TREASURY_VAULT = (asset) => `@treasury:vault:crypto:${asset.toLowerCase()}`;
export const TREASURY_QUARANTINE = (asset) => `@treasury:quarantine:crypto:${asset.toLowerCase()}`;
export const EXPENSE_NETWORK_FEE = (asset) => `@expense:network:${asset.toLowerCase()}`;
const HOT_CLASSES = new Set(['deposit', 'running_hot', 'dirty_hot', 'fee']);
/** Which ledger account a desk class's balance belongs to. */
export function ledgerAccountForClass(cls, asset) {
  if (!DESK_CLASSES.includes(cls)) throw new Error(`not a desk class: ${cls}`);
  if (cls === 'treasury') return TREASURY_VAULT(asset);
  if (cls === 'frozen') return TREASURY_QUARANTINE(asset);
  return TREASURY_HOT(asset);
}
// The invariant that decides whether an on-chain move is ledger-visible:
// a move posts if and only if it crosses an economic boundary.
//   deposit → running_hot   (sweep)           same account  → no posting
//   deposit → dirty_hot     (sweep)           same account  → no posting
//   running_hot → treasury  (the noon move)   different     → posting
//   deposit → frozen        (held)            different     → posting
export function postingRequired(fromClass, toClass, asset) {
  return ledgerAccountForClass(fromClass, asset) !== ledgerAccountForClass(toClass, asset);
}
export { HOT_CLASSES };
// ---- KYT: KYT's own word decides ------------------------------------------
//
// The operator's rulings (N-011, 20 Oct 2026; N-031/N-032, 21 Oct; N-101, 4 Nov:
// "KYT is the wallet- Both should be in sync"): the KYT engine screens the
// payment's own money against HIS lines on the KYT screen, and its routing word
// decides; no score ever routes money. The three results the wallet knows:
//
//   clean    → Hot (the clean pool)
//   exposed  → Merchant Hot (the dirty pool): KYT's word is 'apart' (an alert
//              line crossed); under KYT's minimum, a list or a blacklist naming
//              the sender; a criminal or sanctioned source GL names without a
//              dollar figure — Merchant Hot "until checked by hand"
//   pending  → held (fail closed): no rating yet, the engine not answering
//
// The decision itself is orchestrator/src/kyt/route.mjs routePayment(). The
// wallet keeps no lines: they are KYT's, from his settings (N-101).
export const KYT_RESULTS = Object.freeze(['clean', 'exposed', 'pending']);
export const KYT_RESULT_POOL = Object.freeze({ clean: 'clean', exposed: 'dirty' });

// A verdict only permits money movement when it is 'clear' AND unexpired.
export function kytPermitsMovement(check, now = new Date()) {
  if (!check || check.verdict !== 'clear') return false;
  if (!check.expiresAt) return false;
  return new Date(check.expiresAt) > now;
}

// ---- ledger posting events ----------------------------------------------
//
// Wallet events that cross into Formance. Each carries its own idempotency
// reference — the existing TradeMetadata assumed a deal_id, which deposits,
// sweeps, vault moves and fee postings simply do not have.

export const WALLET_EVENTS = Object.freeze({
  DEPOSIT_CLEARED: 'deposit_cleared',
  DEPOSIT_QUARANTINED: 'deposit_quarantined',
  PAYOUT_CONFIRMED: 'payout_confirmed',
  VAULT_MOVE: 'vault_move',
  QUARANTINE_RELEASE: 'quarantine_release',
  QUARANTINE_DISPOSAL: 'quarantine_disposal',
  NETWORK_FEE: 'network_fee',
});

const REFERENCE_PREFIX = Object.freeze({
  [WALLET_EVENTS.DEPOSIT_CLEARED]: 'DEP',
  [WALLET_EVENTS.DEPOSIT_QUARANTINED]: 'DEPQ',
  [WALLET_EVENTS.PAYOUT_CONFIRMED]: 'PAY',
  [WALLET_EVENTS.VAULT_MOVE]: 'VLT',
  [WALLET_EVENTS.QUARANTINE_RELEASE]: 'QREL',
  [WALLET_EVENTS.QUARANTINE_DISPOSAL]: 'QDIS',
  [WALLET_EVENTS.NETWORK_FEE]: 'FEE',
});

// Deterministic, collision-free idempotency key. Submitting the same wallet
// event twice is a Formance reference collision → a safe no-op, which is
// exactly what we want when a watcher replays or a job retries.
export function ledgerReference(event, id) {
  const p = REFERENCE_PREFIX[event];
  if (!p) throw new Error(`unknown wallet event: ${event}`);
  if (id === undefined || id === null || String(id).length === 0) {
    throw new Error('ledger reference requires an id');
  }
  return `${p}-${id}`;
}

// Formance metadata must be FLAT and string-valued. This builder enforces
// both, so a nested object can never silently fail to serialize.
export function walletEventMetadata(input) {
  const out = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'object') {
      throw new Error(`metadata.${k} must be a scalar — Formance metadata is flat strings`);
    }
    out[k] = typeof v === 'bigint' ? v.toString() : String(v);
  }
  if (!out.type) throw new Error('metadata.type is required');
  return out;
}

// ---- numscript template registry ----------------------------------------
//
// Single source of truth for which templates exist. The BFF loader imports
// exactly these, and a test asserts every filename is present on disk — the
// loader previously imported buy.num/sell.num, which do not exist, and that
// broke the Worker build silently at bundle time.

export const NUMSCRIPT_TEMPLATES = Object.freeze({
  trade: 'trade.num',
  transfer: 'transfer.num',
  deposit: 'deposit.num',
  withdrawal: 'withdrawal.num',
  rebalance: 'rebalance.num',
  quoteHold: 'quote-hold.num',
  // wallet-system templates
  walletInflow: 'wallet-inflow.num',
  walletOutflow: 'wallet-outflow.num',
  walletTransfer: 'wallet-transfer.num',
  walletFee: 'wallet-fee.num',
});

// Which template services each wallet ledger event.
export const EVENT_TEMPLATE = Object.freeze({
  [WALLET_EVENTS.DEPOSIT_CLEARED]: 'walletInflow',
  [WALLET_EVENTS.DEPOSIT_QUARANTINED]: 'walletInflow',
  [WALLET_EVENTS.PAYOUT_CONFIRMED]: 'walletOutflow',
  [WALLET_EVENTS.QUARANTINE_DISPOSAL]: 'walletOutflow',
  [WALLET_EVENTS.VAULT_MOVE]: 'walletTransfer',
  [WALLET_EVENTS.QUARANTINE_RELEASE]: 'walletTransfer',
  [WALLET_EVENTS.NETWORK_FEE]: 'walletFee',
});

// ---- signer API ----------------------------------------------------------

export const SIGNER_OPS = Object.freeze({
  DERIVE_ADDRESS: 'derive_address',
  SIGN_SWEEP: 'sign_sweep',
  SIGN_TRANSFER: 'sign_transfer',
  SIGN_PAYOUT: 'sign_payout',
  SIGN_FEE: 'sign_fee',
});

// Which classes each op may draw from — mirrored by the signer's own checks.
// Duplicated deliberately: the contract documents it, the signer ENFORCES it,
// and a test asserts the two agree. Treasury has no op: it is hardware-only.
export const SIGNER_OP_SOURCES = Object.freeze({
  [SIGNER_OPS.DERIVE_ADDRESS]: [],
  [SIGNER_OPS.SIGN_SWEEP]: ['deposit'],
  [SIGNER_OPS.SIGN_TRANSFER]: ['running_hot', 'dirty_hot', 'frozen'],
  [SIGNER_OPS.SIGN_PAYOUT]: ['running_hot', 'dirty_hot', 'frozen'],
  [SIGNER_OPS.SIGN_FEE]: ['fee'],
});

// ---- whose word makes a payout address payable -------------------------------
//
// The Books' "Approve payout address" is the owner's alone (main 99fae9d,
// bff/src/middleware/rbac.ts OWNER_TELEGRAM_ID). The wallet checks the same
// on its side, everywhere it reads the Books' word: an approval recorded
// 'books' by anyone else is a proposal, not an approval (the queue of 23 Oct
// 2026, item 6). One id, here and in admin.custody_books_owner() (db/0092).
export const BOOKS_OWNER_TELEGRAM_ID = '5313237796';
/** Whether a payout address is payable: approved in the Books, by the owner. */
export function payoutApprovedByOwner(via, by) {
  return via === 'books' && String(by ?? '') === BOOKS_OWNER_TELEGRAM_ID;
}

// ---- who pays whom (the operator, 22 Oct 2026) -----------------------------
//
// "all customer payout will go from hot or merchant hot" — "1-yes, to whom we
// have marked as merchants in the books app, risky & flagged both applies
// here. 2- yes ofc." Hot pays any Books-approved payout address; Merchant Hot
// and Flagged pay only a Books-approved address of a party the Books mark
// MERCHANT (admin.parties.counterparty_role = 'merchant'). Each source has
// caps of its own (db/0089); the door and the orchestrator read the same rule.

// ---- fees: the TRX each float keeps (the operator's decision 1, 23 Oct 2026) ----
//
// A float pays its own chain fee when the energy desk cannot: on mainnet one
// USDT transfer burns ≈65,000 energy to an address holding USDT and ≈130,000
// to one holding none (Energy, 23 Oct 2026) — priced live from the chain's
// energy fee, never from a constant in TRX. The desk keeps each float above
// its floor (db/0097, HIS figures on Flow → Caps; these are the seeded
// defaults), refilled from the FEE wallet under half, and tops a source up
// to the burn cost before a sweep that lands on burn.
// Flagged too (fee-floors review, 26 Oct 2026): it is a payout source and pays its own fee (db/0098 adds its row).
export const FEE_FLOOR_CLASSES = Object.freeze(['running_hot', 'dirty_hot', 'frozen', 'treasury']);
export const FEE_FLOOR_DEFAULTS_TRX = Object.freeze({ running_hot: '100', dirty_hot: '50', frozen: '20', treasury: '20' });
/** The most a floor may be (the table's own CHECK, db/0097): a figure above it is refused before anything is written. */
export const FEE_FLOOR_MAX_TRX = '10000';
export const FEE_READY = Object.freeze({
  /** One USDT transfer with no energy, to an address holding no USDT — the worst case, in energy units. */
  worstCaseEnergy: 130_000n,
  /** The same, to an address that already holds USDT (Energy's figure, 24 Oct 2026). */
  holderEnergy: 65_000n,
  /** Its bandwidth, in bytes, when the free daily 600 are spent. */
  bandwidthBytes: 350n,
  /** Added to a top-up so a fee priced a block ago still clears. */
  marginPct: 10n,
  /** Under this much TRX a source is refused a NEW payout before the Nano is touched — "never fails" per Energy. */
  minTrx: '15',
});


/** The classes a dashboard payout may leave. */
export const PAYOUT_SOURCE_CLASSES = Object.freeze(['running_hot', 'dirty_hot', 'frozen']);
/** The sources that pay merchants only. */
export const MERCHANT_ONLY_SOURCES = Object.freeze(['dirty_hot', 'frozen']);
/** Whether money leaving `sourceClass` may go to a payee of `partyRole` ('customer' | 'merchant'). */
export function sourceMayPay(sourceClass, partyRole) {
  if (!PAYOUT_SOURCE_CLASSES.includes(sourceClass)) return false;
  if (MERCHANT_ONLY_SOURCES.includes(sourceClass)) return partyRole === 'merchant';
  return partyRole === 'customer' || partyRole === 'merchant';
}
/**
 * The desk's own wallets a dashboard move may fill, by where the money
 * leaves: Hot fills the vault, Flagged and Merchant Hot (the flow's steps 7
 * and its moves by decision); the vault REFILLS Hot — and nothing else (the
 * queue of 23 Oct 2026, item 3: "the Treasury → Hot refill, signed by the
 * Treasury key" — two of its three, the card a `refill`). The vault pays
 * nobody. Flagged gives back to Hot the desk's own money Hot moved into it
 * (db/0109, item 5: N-088's leftover) — never a depositor's.
 */
export const FILL_BY_SOURCE = Object.freeze({
  running_hot: Object.freeze(['treasury', 'frozen', 'dirty_hot']),
  treasury: Object.freeze(['running_hot']),
  frozen: Object.freeze(['running_hot']),
});
export function sourceMayFill(sourceClass, destinationClass) {
  return (FILL_BY_SOURCE[sourceClass] ?? []).includes(destinationClass);
}
/** Every class a dashboard move may leave: the payout sources, and the vault for a refill. */
export const MOVE_SOURCE_CLASSES_ALL = Object.freeze([...PAYOUT_SOURCE_CLASSES, 'treasury']);

// ---- BFF ↔ Mini App routes ----------------------------------------------
//
// Mounted under the existing Hono app; every route inherits telegramAuth() +
// loadAdmin() + requirePermission(). Permission strings follow the existing
// 'module.action' convention.

export const WALLET_API = Object.freeze({
  BALANCES: '/api/wallets/balances',
  DEPOSIT_ADDRESS: '/api/wallets/parties/:slug/deposit-address',
  DEPOSITS: '/api/wallets/deposits',
  APPROVALS: '/api/wallets/approvals',
  COSIGN_LINK: '/api/wallets/approvals/:txid/cosign-link',
  WHITELIST: '/api/wallets/whitelist',
  WHITELIST_APPROVAL_LINK: '/api/wallets/whitelist/:id/approval-link',
  PAYOUTS: '/api/wallets/payouts',
  SUMMARY: '/api/wallets/summary',
});

// ---- BFF ↔ co-sign page --------------------------------------------------
//
// A SEPARATE PREFIX, not a corner of WALLET_API. These are the only routes a
// URL-borne capability token can reach, and they share a router with nothing
// that Telegram initData authenticates. Putting them under /api/wallets would
// mean one of the two schemes running first on every wallet request, and every
// way of resolving that ends with `Authorization: Bearer <anything>` reaching a
// route that was never meant to accept it.
//
// The whitelist the page anchors against is returned INSIDE the approval rather
// than from a route of its own: that binds its disclosure to one transaction's
// lifetime, and removes the failure where a separate fetch returning nothing
// makes every legitimate payout look unrecognised.
export const COSIGN_API = Object.freeze({
  APPROVAL: '/api/cosign/approvals/:txid',
  SIGNATURE: '/api/cosign/approvals/:txid/signature',
  // Making a payout address payable. A SIGNED MESSAGE, not a transaction — an
  // on-chain marker would publish the link between a desk wallet and a
  // counterparty before any payment is made, and cost TRX per address, to prove
  // exactly what a signature proves. See signer/src/whitelist-approval.mjs.
  WHITELIST_ENTRY: '/api/cosign/whitelist/:id',
  WHITELIST_SIGNATURE: '/api/cosign/whitelist/:id/signature',
});

export const WALLET_PERMISSIONS = Object.freeze({
  READ: 'wallets.read',
  ISSUE_ADDRESS: 'wallets.issue_address',
  PROPOSE_PAYOUT: 'wallets.propose_payout',
  COSIGN: 'wallets.cosign',
  MANAGE_WHITELIST: 'wallets.manage_whitelist',
  RESOLVE_QUARANTINE: 'wallets.resolve_quarantine',
});

// ---- Telegram bot surface ------------------------------------------------
//
// Read-only actions resolve in the bot; anything that signs deep-links into
// the Mini App, because signing needs TronLink + Ledger inside the webview.

export const BOT_COMMANDS = Object.freeze([
  { command: 'balances', description: 'Wallet balances by class' },
  { command: 'summary', description: "Today's deposits, payouts and exceptions" },
  { command: 'deposits', description: 'Recent and pending deposits' },
  { command: 'approvals', description: 'Transactions awaiting your co-signature' },
  { command: 'whitelist', description: 'Payout address book' },
  { command: 'help', description: 'What this bot can do' },
]);

// Persistent reply-keyboard: always visible, one tap, reusable.
export const BOT_KEYBOARD = Object.freeze([
  ['💰 Balances', '📥 Deposits'],
  ['✅ Approvals', '📋 Whitelist'],
  ['📊 Summary'],
]);

// Inline callback_data values. Kept short (Telegram caps callback_data at 64
// bytes) and prefixed so the router can dispatch on the prefix.
export const BOT_CALLBACKS = Object.freeze({
  VIEW_PARTY: 'p:',        // p:<slug>
  VIEW_DEPOSIT: 'd:',      // d:<deposit_id>
  OPEN_COSIGN: 'c:',       // c:<txid>       → deep-links to the Mini App
  OPEN_CASE: 'q:',         // q:<case_id>
  APPROVE_WHITELIST: 'w:', // w:<entry_id>   → deep-links to the Mini App
  REFRESH: 'r:',           // r:<view>
});

export function callbackData(prefix, id) {
  const s = `${prefix}${id}`;
  if (new TextEncoder().encode(s).length > 64) {
    throw new Error('callback_data exceeds Telegram 64-byte limit');
  }
  return s;
}

// Actions that must NOT be completable inside the bot — they require the
// hardware-backed signing surface.
export const BOT_DEEPLINK_ONLY = Object.freeze([
  BOT_CALLBACKS.OPEN_COSIGN,
  BOT_CALLBACKS.APPROVE_WHITELIST,
]);

// -------------------------------------------------------------------------
// The move caps (audit of 21 Oct 2026, risk 13).
//
// One move may carry at most `perMove`; the Dubai day may carry at most
// `perDay`, counting every sweep, move and payout made that day that did not
// fail. The same line is said at the dashboard's door and by the orchestrator
// when it will not build a card, so the operator hears one voice. Amounts in
// base units of the asset; figures international (1,008,212.5).
// -------------------------------------------------------------------------

export const DUBAI_OFFSET_MS = 4 * 60 * 60 * 1000;   // Asia/Dubai: UTC+4, no daylight time

/** Midnight of the Dubai day that holds `nowMs`, in epoch ms. */
export function dubaiDayStartMs(nowMs) {
  if (typeof nowMs !== 'number' || !Number.isFinite(nowMs)) throw new Error('dubaiDayStartMs needs the time in ms');
  const local = nowMs + DUBAI_OFFSET_MS;
  return local - (((local % 86_400_000) + 86_400_000) % 86_400_000) - DUBAI_OFFSET_MS;
}


/** A figure for a cap line: "1,008,212.5 USDT". */
/**
 * The Dubai day `ms` falls in, as 'YYYY-MM-DD' — the key a move's amount is
 * reserved against (admin.custody_sweeps.cap_day, db/0085). One spelling,
 * computed here, so the dashboard's door and the orchestrator reserve
 * against the same day.
 */
export function dubaiDayKey(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) throw new Error('dubaiDayKey needs a time in ms');
  return new Date(ms + DUBAI_OFFSET_MS).toISOString().slice(0, 10);
}

export function capFigure(asset, units) {
  const text = fromBaseUnits(asset, units);
  const n = Number(text);
  const shown = Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 6 }) : text;
  return `${shown} ${ASSETS[asset]?.token ?? asset}`;
}

/**
 * Why a move is over a cap, as the one line that is said — or null.
 * `usedToday` is what the day already carries WITHOUT this move.
 * `firstPayout`, when given: the cap on a payout to an address no payout has
 * reached yet (`unproven`), db/0084.
 */
export function capRefusal({ asset = 'USDTTRC', amount, usedToday, perMove, perDay, firstPayout = undefined }) {
  for (const [k, v] of Object.entries({ amount, usedToday, perMove, perDay })) {
    if (typeof v !== 'bigint' || v < 0n) throw new Error(`capRefusal needs ${k} as a non-negative bigint of base units`);
  }
  if (firstPayout !== undefined && (typeof firstPayout?.cap !== 'bigint' || firstPayout.cap < 0n || typeof firstPayout.unproven !== 'boolean')) {
    throw new Error('capRefusal needs firstPayout as { cap: bigint, unproven: boolean }');
  }
  const figures = { asset, amount, usedToday, perMove, perDay, firstPayoutCap: firstPayout?.cap ?? 0n };
  if (amount > perMove) return { reason: 'over_move_cap', line: capLine('over_move_cap', figures) };
  if (firstPayout?.unproven && amount > firstPayout.cap) return { reason: 'over_first_payout_cap', line: capLine('over_first_payout_cap', figures) };
  if (usedToday + amount > perDay) return { reason: 'over_day_cap', line: capLine('over_day_cap', figures) };
  return null;
}

/**
 * The one line each refusal of a move is said in — by the dashboard's door,
 * by the orchestrator's hold, and for what the store's reservation
 * (admin.custody_reserve_move / custody_reserve_cap, db/0085) answers. Null
 * for a word this table does not know.
 */
/**
 * The whitelist's test (db/0098; the operator, 24 Oct 2026): a payout address
 * the owner approved receives exactly this first, and nothing else until the
 * test lands on chain AND an operator presses Received. Then it is whitelisted.
 */
export const TEST_PAYMENT_USDT = '10';
/** The stages of a payout address, in the order it goes through them (db/0098 admin.custody_payout_stage). */
export const PAYOUT_STAGES = Object.freeze(['not_approved', 'approved', 'test_sent', 'whitelisted']);
export const PAYOUT_STAGE_WORDS = Object.freeze({ not_approved: 'Awaiting the Books', approved: 'Approved', test_sent: 'Test sent', whitelisted: 'Whitelisted' });

export function capLine(reason, { asset = 'USDTTRC', amount = 0n, usedToday = 0n, perMove = 0n, perDay = 0n, firstPayoutCap = 0n, testPayment = 10_000_000n } = {}) {
  switch (reason) {
    case 'test_amount':
      return `First payment to this address is the ${capFigure(asset, testPayment)} test, exactly; this one is ${capFigure(asset, amount)}.`;
    case 'test_pending':
      return `The ${capFigure(asset, testPayment)} test to this address is on its way; nothing else until it lands and you press Received.`;
    case 'test_not_received':
      return `The ${capFigure(asset, testPayment)} test landed — confirm with the customer, then press Received on Payout addresses.`;
    case 'over_move_cap':
      return `Over the per-move cap: ${capFigure(asset, amount)} is more than ${capFigure(asset, perMove)}.`;
    case 'over_first_payout_cap':
      return `First payout to a new address: at most ${capFigure(asset, firstPayoutCap)} until one completes; this one is ${capFigure(asset, amount)}.`;
    case 'over_day_cap':
      return `Over the daily cap: ${capFigure(asset, usedToday)} already today; ${capFigure(asset, amount)} more passes ${capFigure(asset, perDay)}.`;
    case 'not_approved':
      return 'The payout address is not approved in the Books.';
    case 'first_payout_pending':
      return 'Another payout to this new address is still open; one at a time until the first completes.';
    case 'held_for_missing':
      return 'This payout from Flagged names no depositor, so it is not carded, and Flagged releases wait behind it. Its depositor must be named first.';
    default:
      return null;
  }
}
